function createTextMeasurer(fontFamily: string, fontSize: number) {
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('2d');

  if (!context) {
    throw new Error('Canvas 2D context not available');
  }

  context.font = `${fontSize}px ${fontFamily}`;

  return {
    measureText: (text: string) => context.measureText(text),
    destroy: () => canvas.remove(),
  };
}

interface TruncateTextOptions {
  maxLines: number;
  containerWidth: number;
  fontSize: number;
  fontFamily: string;
  lineHeight: number;
}

/** Truncates text to fit within a specified number of lines and container width */
export function truncateText(
  text: string,
  options: TruncateTextOptions
): string {
  if (!text || options.maxLines < 0 || options.containerWidth < 0) {
    return text || '';
  }

  const measurer = createTextMeasurer(options.fontFamily, options.fontSize);
  const lineHeightPx = options.fontSize * options.lineHeight;
  const maxHeight = lineHeightPx * options.maxLines;

  try {
    const textWidth = measurer.measureText(text).width;
    const estimatedHeight = (textWidth / options.containerWidth) * lineHeightPx;

    if (estimatedHeight <= maxHeight) {
      return text;
    }

    let left = 0;
    let right = text.length;
    let bestResult = '';

    while (left <= right) {
      const mid = Math.floor((left + right) / 2);
      let testText = text.substring(0, mid);

      if (mid < text.length) {
        // Find the last word boundary
        const lastSpaceIndex = testText.lastIndexOf(' ');
        if (lastSpaceIndex > 0 && lastSpaceIndex > mid * 0.7) {
          testText = testText.substring(0, lastSpaceIndex);
        }
      }

      const trimmedText = testText + '...';
      const textWidth = measurer.measureText(trimmedText).width;
      const estimatedHeight = (textWidth / options.containerWidth) * lineHeightPx;

      if (estimatedHeight <= maxHeight) {
        bestResult = trimmedText;
        left = mid + 1;
      } else {
        right = mid - 1;
      }
    }

    return bestResult;
  } finally {
    measurer.destroy();
  }
}
